import { useEffect, useRef, useState } from 'react';
import { useVoiceSession, shouldShowReconnectCue } from './hooks/useVoiceSession';
import { TalkingLogo } from './components/TalkingLogo';

/** Read the session id from the URL (?sessionId=...) if present. */
function initialSessionId(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('sessionId') ?? '';
}

/** Format elapsed seconds as a call clock: `m:ss`, or `h:mm:ss` past an hour. */
function fmtClock(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

export function App() {
  const [sessionId, setSessionId] = useState(initialSessionId);
  const { status, error, speaking, start } = useVoiceSession();

  // Running call timer. Starts the first time the session goes 'live' and
  // ticks each second; frozen once the session ends. Anchored to a ref so a
  // transient 'reconnecting' blip does not reset the clock.
  const callStartRef = useRef<number | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (status === 'live' && callStartRef.current === null) {
      callStartRef.current = Date.now();
    }
    if (callStartRef.current === null) return;
    if (status === 'ended') return; // freeze the clock on end
    const tick = () => {
      if (callStartRef.current !== null) {
        setElapsedSec(Math.floor((Date.now() - callStartRef.current) / 1000));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status]);
  const showTimer = callStartRef.current !== null;

  /**
   * Auto-start once on mount when a sessionId is present in the URL (Req 4.1).
   * The ref latch guarantees `start` runs at most once even though React 18
   * StrictMode double-invokes effects in dev (main.tsx wraps <App> in
   * <React.StrictMode>) and across re-renders (Req 4.6). With no id we do not
   * connect and fall through to the idle state (Req 4.3).
   */
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    if (!sessionId) return;
    startedRef.current = true;
    void start(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSession = Boolean(sessionId);

  return (
    <div className="min-h-screen bg-bg text-content flex flex-col items-center justify-center gap-6 p-6">
      {/* Primary content: the centered talking logo (Req 1.1). */}
      {/* 100% bigger than the 220px default (Req: enlarge animated logo). */}
      <TalkingLogo speaking={speaking} size={440} />

      {hasSession ? (
        <div className="flex flex-col items-center gap-3">
          {/* Running call timer — visible once the call has gone live. */}
          {showTimer && (
            <p
              className="font-mono text-2xl tabular-nums text-content tracking-wide"
              aria-label="Call duration"
              title="Call duration"
            >
              {fmtClock(elapsedSec)}
            </p>
          )}
          {/* Surface the error state clearly (Req 4.5). */}
          {status === 'error' && error && (
            <p role="alert" className="text-danger text-sm text-center max-w-sm">
              {error}
            </p>
          )}

          {/* Transient reconnect cue: the socket dropped and we're reopening to
              the same session (seamless when a standby was warm). Keep it low-key
              so a sub-second blip doesn't alarm the user. */}
          {shouldShowReconnectCue(status) && (
            <p role="status" className="text-content-muted text-sm">
              Reconnecting…
            </p>
          )}

          {status === 'ended' && (
            <p className="text-content-muted text-sm">Session ended.</p>
          )}
        </div>
      ) : (
        /* No session id in the URL: clear, minimal idle state (Req 4.3). */
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-content-muted text-sm max-w-sm">
            Open this page with a session link to begin. The conversation starts
            automatically once connected.
          </p>
          {/* Optional manual-entry fallback — no Start button gates the URL path. */}
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (sessionId) {
                startedRef.current = true;
                void start(sessionId);
              }
            }}
          >
            <input
              className="flex-1 rounded-md bg-surface border border-border px-3 py-2 text-sm text-content outline-none focus:ring-2 focus:ring-accent"
              placeholder="session id"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              aria-label="Session id"
            />
            <button
              type="submit"
              className="rounded-md bg-surface border border-border px-4 py-2 text-sm font-medium text-accent hover:bg-surface-raised disabled:opacity-50"
              disabled={!sessionId}
            >
              Join
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
